import React from "react";
import { Box, alpha } from "@mui/material";
import { Explore, Straighten } from "@mui/icons-material";

import { CLOCK_THEMES } from "./MapClock";
import { glassSx } from "./overlayStyles";
import { MONO } from "./LocationStatusBar";

function toRad(deg) {
  return deg * Math.PI / 180;
}
function toDeg(rad) {
  return rad * 180 / Math.PI;
}

// Azimuth / distance from the selected radar station to the cursor.
// Sits just above LocationStatusBar. mode: 'live' (Radar) | 'replay' (Review)
const AzimuthDistanceStatusBar = ({ from, to, mode = "live" }) => {
  if (!from || !to) return null;

  const { accent } = CLOCK_THEMES[mode] || CLOCK_THEMES.live;

  const { latitude: lat1, longitude: lon1 } = from;
  const { lat: lat2, lng: lon2 } = to;

  // Haversine formula for distance
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  // Azimuth calculation
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
  let azimuth = toDeg(Math.atan2(y, x));
  azimuth = (azimuth + 360) % 360;

  const item = (Icon, label, value, unit) => (
    <Box display="flex" alignItems="center" gap={0.75}>
      <Icon sx={{ fontSize: "1rem", color: accent }} />
      <Box>
        <Box sx={{ fontSize: "0.58rem", fontWeight: 800, letterSpacing: "0.14em", color: alpha("#fff", 0.6), lineHeight: 1.2 }}>
          {label}
        </Box>
        <Box sx={{ fontFamily: MONO, fontSize: "0.85rem", fontWeight: 700, color: accent, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
          {value}
          <Box component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, color: alpha("#fff", 0.7), ml: 0.25 }}>
            {unit}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      position="absolute"
      bottom={64}
      left={12}
      zIndex={1300}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.25,
        py: 0.6,
        borderRadius: "10px",
        color: "#fff",
        userSelect: "none",
        pointerEvents: "none",
        ...glassSx(accent),
      }}
    >
      {item(Explore, "AZIMUTH", azimuth.toFixed(1), "°")}
      <Box sx={{ width: "1px", alignSelf: "stretch", my: 0.25, backgroundColor: alpha("#fff", 0.18) }} />
      {item(Straighten, "DISTANCE", distance.toFixed(2), "km")}
    </Box>
  );
};

export default AzimuthDistanceStatusBar;
